(() => {
  'use strict';
  if (window.LRSChapters) return;
  const instances = new Map();
  let frame = 0, inputMode = 'keyboard';
  const mobile = () => window.innerWidth <= 800;
  function queue() {if (instances.size && !frame) frame = requestAnimationFrame(update);}

  function revealLink(state, link) {
    if (!link || !mobile()) return;
    const bounds = state.index.getBoundingClientRect();
    const item = link.getBoundingClientRect();
    if (item.left < bounds.left + 16 || item.right > bounds.right - 16) {
      const left = state.index.scrollLeft + item.left - bounds.left - 20;
      state.index.scrollTo?.({left:Math.max(0, left), behavior:'auto'});
    }
  }

  function update() {
    frame = 0;
    const isMobile = mobile();
    const height = window.innerHeight;
    const marker = Math.min(height * .33, 300);
    const midpoint = height * .5;
    const offset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--lrs-header-offset'));
    const header = Number.isFinite(offset) ? offset : 76;
    const states = [...instances.values()].filter(state => state.section.isConnected);
    let owner = null;
    if (isMobile) {
      // Keyboard focus keeps its navigation available until focus leaves it.
      owner = inputMode === 'keyboard' ? states.find(state => state.index.contains(document.activeElement)) || null : null;
      if (!owner) {
        const candidates = states.map(state => ({state, rect:state.section.getBoundingClientRect()}))
          .filter(({rect}) => rect.top <= height * .75 && rect.bottom > header + 80);
        candidates.sort((a, b) => {
          const distance = rect => Math.max(rect.top - midpoint, midpoint - rect.bottom, 0);
          return distance(a.rect) - distance(b.rect) || Math.abs(a.rect.top - midpoint) - Math.abs(b.rect.top - midpoint);
        });
        owner = candidates[0]?.state || null;
      }
    }
    states.forEach(state => {
      const {section, index, chapters, links} = state;
      const docked = isMobile && state === owner;
      const wasDocked = index.classList.contains('is-docked');
      index.classList.toggle('is-docked', docked);
      if (isMobile && !docked && inputMode === 'pointer' && index.contains(document.activeElement)) document.activeElement.blur();
      index.inert = isMobile && !docked;
      if (isMobile && !docked) index.setAttribute('aria-hidden','true'); else index.removeAttribute('aria-hidden');
      if (isMobile) {
        const dockHeight = Math.ceil(index.getBoundingClientRect().height);
        if (dockHeight !== state.height) {
          state.height = dockHeight;
          section.style.setProperty('--lrs-chapter-dock-height', `${dockHeight}px`);
        }
      } else if (state.height !== null) {
        state.height = null;
        section.style.removeProperty('--lrs-chapter-dock-height');
      }
      let active = chapters[0];
      chapters.forEach(chapter => {if (chapter.getBoundingClientRect().top <= marker) active = chapter;});
      links.forEach(link => {
        if (link.hash === `#${active.id}`) link.setAttribute('aria-current','step'); else link.removeAttribute('aria-current');
      });
      // Automatic horizontal scrolling only follows a chapter change or a newly
      // visible dock, so a visitor's own horizontal swipes remain undisturbed.
      if (docked && (active !== state.active || !wasDocked)) revealLink(state, links.find(link => link.hash === `#${active.id}`));
      state.active = active;
    });
  }

  function destroy(section) {
    const state = instances.get(section);
    if (!state) return;
    state.abort.abort(); state.resize?.disconnect();
    state.index.classList.remove('is-docked');
    state.index.inert = false; state.index.removeAttribute('aria-hidden');
    section.classList.remove('chapters-enhanced');
    section.style.removeProperty('--lrs-chapter-dock-height');
    instances.delete(section);
    cancelAnimationFrame(frame); frame = 0;
    if (instances.size) queue();
  }

  function init(root = document) {
    const sections = [...root.querySelectorAll('[data-chapters]')];
    if (root.matches?.('[data-chapters]')) sections.unshift(root);
    sections.forEach(section => {
      if (section.closest('[data-lrs-process-horizontal]')) return;
      if (instances.has(section)) return;
      const index = section.querySelector('.chapter-index');
      const chapters = [...section.querySelectorAll('[data-chapter]')];
      if (!index || !chapters.length) return;
      const abort = new AbortController();
      const state = {section, index, chapters, links:[...index.querySelectorAll('a')], abort, active:null, height:null, resize:null};
      instances.set(section, state);
      section.classList.add('chapters-enhanced');
      index.addEventListener('focusin', event => {queue(); revealLink(state, event.target.closest('a'));}, {signal:abort.signal});
      index.addEventListener('focusout', queue, {signal:abort.signal});
      section.addEventListener('shopify:block:select', event => {
        event.target.closest('[data-chapter]')?.scrollIntoView({behavior:'auto', block:'start'});
        queue();
      }, {signal:abort.signal});
      if (typeof ResizeObserver === 'function') {
        state.resize = new ResizeObserver(queue);
        state.resize.observe(index);
      }
    });
    cancelAnimationFrame(frame); frame = 0;
    update();
  }

  window.LRSChapters = {init, destroy};
  window.addEventListener('scroll', queue, {passive:true});
  window.addEventListener('resize', queue, {passive:true});
  document.addEventListener('pointerdown', () => {inputMode = 'pointer'; queue();}, {passive:true, capture:true});
  document.addEventListener('keydown', () => {inputMode = 'keyboard'; queue();}, {capture:true});
  document.addEventListener('lrs:headerchange', queue);
  document.addEventListener('shopify:section:reorder', queue);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    [...instances.keys()].forEach(section => {if (event.target === section || event.target.contains(section)) destroy(section);});
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once:true}); else init();
})();
